import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import api from "../../api";

import CreatePrediction from "./CreatePrediction";
import PredictionTable from "./PredictionTable";
import PredictionCards from "./PredictionCards";
import Pagination from "./Pagination";
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { toast } from "../shared/ToastProvider";
import EmptyState from "../shared/EmptyState";
import DeleteConfirmation from "../shared/DeleteConfirmation";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

const Prediction = () => {
    const [items, setItems] = useState([]);
    const [comments, setComments] = useState([]);
    const [form, setForm] = useState({
        predicted_direction: "",
        predicted_percentage_change: "",
        predicted_time_frame: "",
        actual_direction: "",
        actual_percentage_change: "",
        score: "",
        evaluated_at: "",
        fk_comment_id: "",
    });
    const [editId, setEditId] = useState(null);
    const location = useLocation();
    const [filterPredictorId, setFilterPredictorId] = useState(null);
    const [filterDates, setFilterDates] = useState({
        minDate: "",
        maxDate: "",
    });

    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 100;

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Charger les données des prédictions avec ou sans filtre
    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const predictorId = query.get('predictor_id');
        setFilterPredictorId(predictorId);

        const fetchPredictions = async () => {
            setLoading(true);
            try {
                let response;
                const params = {};

                if (filterDates.minDate) params.min_evaluated_at = filterDates.minDate;
                if (filterDates.maxDate) params.max_evaluated_at = filterDates.maxDate;

                if (predictorId) {
                    response = await api.get(`/predictions/predictor/${predictorId}`, { params });
                } else {
                    response = await api.get('/predictions', { params });
                }

                setItems(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération des prédictions', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPredictions();
    }, [location.search, filterDates]);

    // Charger les commentaires
    useEffect(() => {
        const fetchComments = async () => {
            try {
                const response = await api.get('/comments');
                setComments(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération des commentaires', error);
            }
        };
        fetchComments();
    }, []);

    // Gérer le changement des valeurs de filtre
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterDates({
            ...filterDates,
            [name]: value,
        });
    };

    // Réinitialiser le filtre date
    const resetFilterDate = () => {
        setFilterDates({ minDate: "", maxDate: "" });
    };

    // Réinitialiser le filtre et charger toutes les données
    const resetFilter = () => {
        setFilterPredictorId(null);
        const fetchAllPredictions = async () => {
            setLoading(true);
            try {
                const response = await api.get('/predictions');
                setItems(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération des prédictions', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllPredictions();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: name === 'evaluated_at' && value ? value : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const formattedForm = {
                ...form,
                evaluated_at: form.evaluated_at
                    ? new Date(form.evaluated_at).toISOString()
                    : "",
            };

            if (editId) {
                await api.put(`/predictions/${editId}`, formattedForm);
                setEditId(null);
                toast.success('Prédiction mise à jour avec succès');
            } else {
                await api.post("/predictions", formattedForm);
                toast.success('Prédiction ajoutée avec succès');
            }
            const query = new URLSearchParams(location.search);
            const predictorId = query.get('predictor_id');
            const response = await api.get('/predictions/', {
                params: predictorId ? { fk_predictor_id: predictorId } : {},
            });
            setItems(response.data);

            setForm({
                predicted_direction: "",
                predicted_percentage_change: "",
                predicted_time_frame: "",
                actual_direction: "",
                actual_percentage_change: "",
                score: "",
                evaluated_at: "",
                fk_comment_id: "",
            });
        } catch (error) {
            toast.error("Erreur lors de la soumission");
            console.error("Erreur lors de la soumission du formulaire", error);
        }
    };

    const handleEdit = (item) => {
        setForm(item);
        setEditId(item.prediction_id);
    };

    const handleDeleteRequest = (id) => {
        setDeleteTarget(id);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        setIsDeleteDialogOpen(false);
        if (!deleteTarget) return;
        try {
            await api.delete(`/predictions/${deleteTarget}`);
            const query = new URLSearchParams(location.search);
            const predictorId = query.get('predictor_id');
            const response = await api.get('/predictions/', {
                params: predictorId ? { fk_predictor_id: predictorId } : {},
            });
            setItems(response.data);
            toast.success('Prédiction supprimée avec succès');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
            console.error("Erreur lors de la suppression de la prédiction", error);
        } finally {
            setDeleteTarget(null);
        }
    };

    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);

    const handleNextPage = () => {
        if (currentPage < Math.ceil(items.length / rowsPerPage)) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <Container className="py-6 md:py-10">
            <PageHeader title="Prédictions" />

            {/* Formulaire pour filtrer par dates */}
            <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="grid w-full gap-1.5">
                        <Label htmlFor="filter-min-date">Date minimum</Label>
                        <Input
                            id="filter-min-date"
                            type="date"
                            name="minDate"
                            value={filterDates.minDate}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <div className="grid w-full gap-1.5">
                        <Label htmlFor="filter-max-date">Date maximum</Label>
                        <Input
                            id="filter-max-date"
                            type="date"
                            name="maxDate"
                            value={filterDates.maxDate}
                            onChange={handleFilterChange}
                        />
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={resetFilterDate}>
                    Réinitialiser les dates
                </Button>
            </div>

            {/* Formulaire pour création/mise à jour */}
            <CreatePrediction
                form={form}
                setForm={setForm}
                editId={editId}
                setEditId={setEditId}
                setItems={setItems}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                comments={comments}
            />

            {filterPredictorId && (
                <div className="mb-4">
                    <p className="text-sm text-muted-foreground mb-2">
                        Filtré par ID de prédicteur : <strong>{filterPredictorId}</strong>
                    </p>
                    <Button variant="outline" size="sm" onClick={resetFilter}>
                        Réinitialiser le filtre
                    </Button>
                </div>
            )}

            {items.length === 0 && !loading ? (
                <EmptyState
                    title="Aucune prédiction trouvée"
                    subtitle="Ajoutez votre première prédiction pour commencer."
                    actionLabel="Ajouter"
                    onAction={() => document.getElementById('prediction-predicted-direction')?.focus()}
                />
            ) : (
                <>
                    <div className="hidden md:block">
                        <PredictionTable
                            items={items}
                            setEditId={setEditId}
                            setForm={setForm}
                            handleFilterChange={handleFilterChange}
                            filterDates={filterDates}
                            resetFilterDate={resetFilterDate}
                            filterPredictorId={filterPredictorId}
                            resetFilter={resetFilter}
                            currentItems={currentItems}
                            handleEdit={handleEdit}
                            handleDelete={handleDeleteRequest}
                        />
                    </div>
                    <div className="md:hidden">
                        <PredictionCards
                            items={currentItems}
                            loading={loading}
                            onEdit={handleEdit}
                            onDeleteRequest={(id) => handleDeleteRequest(id)}
                        />
                    </div>
                    <Pagination
                        items={items}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        handlePageClick={handlePageClick}
                    />
                </>
            )}

            <DeleteConfirmation
                isOpen={isDeleteDialogOpen}
                onClose={() => { setIsDeleteDialogOpen(false); setDeleteTarget(null); }}
                onConfirm={handleDeleteConfirmed}
                title="Confirmer la suppression"
                description="Voulez-vous vraiment supprimer cette prédiction ? Cette action est irréversible."
                confirmText="Supprimer"
            />
        </Container>
    );
};

export default Prediction;
